import { useCallback, useMemo } from 'react';
import cn from 'classnames';

import { IDecisionPreview } from 'ApiPackage/SearchModule/interfaces/IDecisionPreview';
import { DecisionSubjectTypesEnum } from 'PassportKnmPackage/MainModule/enums/DecisionSubjectTypesEnum';
import { Checkbox } from 'UIPackage/MainModule/components/Checkbox';
import { parseErknmValue } from 'PassportKnmPackage/MainModule/common/parseErknmValue';

import styles from '../styles.module.scss';
import { KnmSubtitle } from '../KnmSubtitle';

interface IDecisionFace {
  passportData?: IDecisionPreview;
}

export const DecisionFace = ({ passportData }: IDecisionFace) => {
  const isNotNkoForeignAgentsRegistry =
    !passportData?.organization?.nkoForeignAgentsRegistryExclusionDate &&
    !passportData?.organization?.nkoForeignAgentsRegistryInclusionDate &&
    !passportData?.organization?.nkoForeignAgentsRegistryNumber;
  const isNotMsp = !passportData?.organization?.mspCode || passportData?.organization?.mspCode === 'NON_EXIST';
  const isNotSonko = !passportData?.organization?.isSonko;
  const isNotPresentInItRegistry = !passportData?.organization?.isPresentInItRegistry;
  const organizationTypeTitle = passportData?.organization?.organizationType?.title;
  const isUR = organizationTypeTitle === DecisionSubjectTypesEnum.UR;
  const isIUR = organizationTypeTitle === DecisionSubjectTypesEnum.IUR;
  const isIP = organizationTypeTitle === DecisionSubjectTypesEnum.IP;
  const isFIUR = organizationTypeTitle === DecisionSubjectTypesEnum.FIUR;
  const isIFIZ = organizationTypeTitle === DecisionSubjectTypesEnum.IFIZ;

  const organizationNameColText = useMemo(() => {
    switch (organizationTypeTitle) {
      case DecisionSubjectTypesEnum.FIZ:
      case DecisionSubjectTypesEnum.IFIZ:
        return 'Фамилия, имя, отчество (при наличии)';
      case DecisionSubjectTypesEnum.IUR:
        return 'Наименование';
      case DecisionSubjectTypesEnum.FIUR:
        return 'Наименование организации';
      default:
        return 'Наименование проверяемого лица';
    }
  }, [organizationTypeTitle]);

  const openErvkGovRu = useCallback((cosId: string) => {
    window.open(`https://ervk.gov.ru/objects/${cosId}`, '_blank');
  }, []);

  return (
    <div className={styles.ColumnContent}>
      <KnmSubtitle title="Контролируемое лицо">
        {passportData?.organization?.organizationType?.title && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Тип контролируемого лица</div>
            <div className={styles.ColValue}>{passportData?.organization?.organizationType?.title}</div>
          </div>
        )}
        {passportData?.organization?.inn && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Идентификационный номер налогоплательщика (ИНН)</div>
            <div className={styles.ColValue}>{passportData?.organization?.inn}</div>
          </div>
        )}
        {passportData?.organization?.snils && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Страховой номер индивидуального лицевого счета (СНИЛС)</div>
            <div className={styles.ColValue}>{passportData?.organization?.snils}</div>
          </div>
        )}
        {passportData?.organization?.ogrn && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Основной государственный регистрационный номер (ОГРН)</div>
            <div className={styles.ColValue}>{passportData?.organization?.ogrn}</div>
          </div>
        )}
        {passportData?.organization?.kpp && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Код причины постановки на учет (КПП)</div>
            <div className={styles.ColValue}>{passportData?.organization?.kpp}</div>
          </div>
        )}
        {passportData?.organization?.rafpNza && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Номер записи об аккредитации (НЗА) в РАФП</div>
            <div className={styles.ColValue}>{passportData?.organization?.rafpNza}</div>
          </div>
        )}
        {passportData?.organization?.originalName && (
          <div className={styles.Row}>
            <div className={styles.ColText}>{organizationNameColText}</div>
            <div className={styles.ColValue}>{passportData?.organization?.originalName}</div>
          </div>
        )}
        {passportData?.organization?.document && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Сведения о документе, удостоверяющем личность</div>
            <div className={styles.ColValue}>{passportData?.organization?.document}</div>
          </div>
        )}
        {passportData?.organization?.country && isIUR && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Государство, в котором указанное юридическое лицо зарегистрировано</div>
            <div className={styles.ColValue}>{passportData?.organization?.country?.title}</div>
          </div>
        )}
        {passportData?.organization?.country && isIFIZ && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Гражданство (при наличии)</div>
            <div className={styles.ColValue}>{passportData?.organization?.country?.title}</div>
          </div>
        )}
        {passportData?.organization?.locationAddress && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Адрес места нахождения</div>
            <div className={styles.ColValue}>{passportData?.organization?.locationAddress}</div>
          </div>
        )}
        {passportData?.organization?.workAddress && !isIP && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Место нахождения (осуществления деятельности) контролируемого лица</div>
            <div className={styles.ColValue}>{passportData?.organization?.workAddress}</div>
          </div>
        )}
        {passportData?.organization?.factualAddress && !isIP && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Место фактического осуществления деятельности</div>
            <div className={styles.ColValue}>{passportData?.organization?.factualAddress}</div>
          </div>
        )}
        {passportData?.organization && isUR && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Наличие в реестре НКО, выполняющих функции иностранного агента</div>
            <div className={styles.ColValue}>{isNotNkoForeignAgentsRegistry ? 'Нет' : 'Да'}</div>
          </div>
        )}
        {passportData?.organization && (isUR || isIP || isFIUR) && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Наличие в реестре МСП</div>
            <div className={styles.ColValue}>{isNotMsp ? 'Нет' : 'Да'}</div>
          </div>
        )}
        {passportData?.organization && (isUR || isFIUR) && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Наличие в реестре СОНКО</div>
            <div className={styles.ColValue}>{isNotSonko ? 'Нет' : 'Да'}</div>
          </div>
        )}
        {passportData?.organization && isUR && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Наличие в реестре аккредитованных IT-организаций</div>
            <div className={styles.ColValue}>{isNotPresentInItRegistry ? 'Нет' : 'Да'}</div>
          </div>
        )}
      </KnmSubtitle>
      <KnmSubtitle title="Сведения об объектах контроля">
        {passportData?.objects.map((item, index) => (
          <div key={index}>
            <span className={styles.SectionTitle}>Объект №{index + 1}</span>
            {(item.cosId || item.ervkNumber) && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Номер объекта</div>
                <div
                  className={cn(styles.ColValue, { [styles.ColValueLink]: !!item.cosId })}
                  onClick={() => item.cosId && openErvkGovRu(item.cosId as string)}
                >
                  {item.cosId || item.ervkNumber}
                </div>
              </div>
            )}
            {item.name && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Наименование объекта</div>
                <div className={styles.ColValue}>{item.name}</div>
              </div>
            )}
            <div className={styles.Row}>
              <div className={styles.ColText}>Иное указание места (нет в ФИАС)</div>
              <div className={styles.ColValue}>
                <Checkbox checked={item.notInFIAS} size="medium" type="checkbox" theme="system" disabled={true} />
              </div>
            </div>
            {item.address && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Местонахождение</div>
                <div className={styles.ColValue}>{item.address}</div>
              </div>
            )}
            {item.manualAddress && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Местонахождение</div>
                <div className={styles.ColValue}>{item.manualAddress}</div>
              </div>
            )}
            {item.region && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Субъект РФ</div>
                <div className={styles.ColValue}>{item.region}</div>
              </div>
            )}
            {item.fiasRegion && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Субъект РФ</div>
                <div className={styles.ColValue}>
                  {typeof item.fiasRegion === 'object' ? item.fiasRegion.name : item.fiasRegion}
                </div>
              </div>
            )}
            {item.objectType && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Тип объекта</div>
                <div className={styles.ColValue}>
                  {parseErknmValue<{ title?: string }>(item.objectType?.value || '')?.title}
                </div>
              </div>
            )}
            {item.objectKind && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Вид объекта</div>
                <div className={styles.ColValue}>
                  {parseErknmValue<{ title?: string }>(item.objectKind?.value || '')?.title}
                </div>
              </div>
            )}
            {item.objectSubKind && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Подвид объекта</div>
                <div className={styles.ColValue}>
                  {parseErknmValue<{ title?: string }>(item.objectSubKind?.value || '')?.title}
                </div>
              </div>
            )}
            {item.riskCategory && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Категория риска</div>
                <div className={styles.ColValue}>
                  {parseErknmValue<{ title?: string }>(item.riskCategory?.value || '')?.title}
                </div>
              </div>
            )}
            {item.dangerClass && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Класс опасности</div>
                <div className={styles.ColValue}>
                  {parseErknmValue<{ title?: string }>(item.dangerClass?.value || '')?.title}
                </div>
              </div>
            )}
          </div>
        ))}
      </KnmSubtitle>
    </div>
  );
};
